import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    ...options,
  }).format(new Date(date));
}

export function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatNumber(num: number) {
  return new Intl.NumberFormat('en-US').format(num);
}

export function truncate(str: string, length: number) {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

/** Map numeric device type codes to human-readable names (matches backend DeviceTypeToString). */
const DEVICE_TYPE_MAP: Record<string, string> = {
  '0': 'Rower',
  '1': 'Bike',
  '2': 'Treadmill',
  '255': 'Unknown',
};

export function displayDeviceType(deviceType: string | undefined | null): string {
  if (!deviceType) return '-';
  return DEVICE_TYPE_MAP[deviceType] ?? deviceType;
}

/**
 * Map MachineSubType codes to labels. `0`/NONE and empty are intentionally
 * absent — a machine with no submodel should render nothing at all.
 *
 * No longer treadmill-only: DeviceInfo 1.7 added SSR2, the first ROWER
 * submodel, and moved the field onto `persist.soundwin.device.model.subtype`
 * so rowers can report one. Code ranges are not reserved per modality, so do
 * not infer a device type from a sub-type value.
 *
 * Matches backend MachineSubType (DeviceInfo interface v1.7).
 */
const MACHINE_SUBTYPE_MAP: Record<string, string> = {
  // Treadmill submodels
  '1': 'Victory',
  '2': 'Summit',
  '3': 'Podium',
  '4': 'Justice',
  // Rower submodels (1.7+)
  '5': 'SSR2',
};

/**
 * The canonical value the sub-type facet uses for "no submodel reported".
 *
 * The column stores this three ways — NULL, '' and '0' — and the backend
 * collapses them onto '0' in both the facet vocabulary and the filter
 * predicate. The UI only ever sees and sends this one.
 */
export const SUBTYPE_NONE = '0';

/** True when a raw sub-type value means "no submodel reported". */
export function isNoSubType(subType: string | undefined | null): boolean {
  return !subType || subType === SUBTYPE_NONE;
}

/**
 * Label for a sub-type inside the FILTER MENU, where "no submodel" is a real
 * thing to pick.
 *
 * Deliberately separate from displayDeviceSubType, which returns "" for NONE so
 * that a table row shows no submodel pill at all. Rendering "None" against
 * every bike in the fleet would be noise; offering it as a filter is not,
 * because since DeviceInfo 1.7 a machine reporting no submodel is a real
 * diagnostic set — pre-1.7 firmware, or a probe that never resolved.
 */
export function deviceSubTypeFacetLabel(subType: string | undefined | null): string {
  if (isNoSubType(subType)) return 'NONE';
  return displayDeviceSubType(subType).toUpperCase();
}

/**
 * Which modality each MachineSubType code belongs to.
 *
 * Kept beside MACHINE_SUBTYPE_MAP because it is the same fact from the device
 * interface, and the two must not drift: a submodel added to one without the
 * other is either an unlabelled code or an unfilterable one.
 *
 * Codes are NOT range-reserved per modality — 1-4 being treadmill and 5 being
 * rower is a historical accident, not a rule. The next rower submodel could be
 * 6, or the next treadmill could be. Always look it up here rather than
 * inferring from the number.
 */
const MACHINE_SUBTYPE_MODALITY: Record<string, string> = {
  '1': 'TREADMILL',
  '2': 'TREADMILL',
  '3': 'TREADMILL',
  '4': 'TREADMILL',
  '5': 'ROWER',
};

/**
 * The modality a sub-type code belongs to, uppercased to match the Type facet's
 * labels, or "" for an unknown code.
 *
 * Returning "" rather than guessing keeps an unrecognised submodel out of the
 * filter menu entirely: offering it under the wrong machine is worse than not
 * offering it, because the filter would then return zero rows with nothing on
 * screen explaining why.
 */
export function deviceSubTypeModality(subType: string | undefined | null): string {
  if (!subType) return '';
  return MACHINE_SUBTYPE_MODALITY[subType] ?? '';
}

/**
 * Human label for a device sub-type, or "" when there's nothing to show
 * (empty, or `0`/NONE — any machine that has not reported a submodel).
 * Callers should omit the row/pill entirely on an empty return.
 *
 * An unmapped code falls through to the raw value rather than "" so a submodel
 * added device-side before this map catches up still shows *something*. It
 * reads as a bare number, which is ugly but visibly wrong — better than
 * silently rendering nothing and looking like the device reported NONE.
 */
export function displayDeviceSubType(subType: string | undefined | null): string {
  if (!subType || subType === '0') return '';
  return MACHINE_SUBTYPE_MAP[subType] ?? subType;
}

/**
 * DeviceProtocol codes from DeviceInfo interface 1.6 — how the machine
 * controller is wired, read from `persist.soundwin.device.model.type`.
 *
 * Distinguishes the two kinds of rower by wiring: a UART machine controller and
 * a USB rower whose HID endpoint the kiosk app owns both report
 * DeviceType.ROWER. Since 1.7 sub-type can also identify a rower (SSR2), but
 * the two answer different questions — this is how the controller is attached,
 * sub-type is which model it is.
 */
const DEVICE_PROTOCOL_MAP: Record<string, string> = {
  '0': 'Not detected',
  '1': 'UART',
  '2': 'USB HID',
  '255': 'Unrecognised',
};

/**
 * Human label for a device protocol, or "" when the device has not reported one
 * (firmware older than DeviceInfo 1.6 omits the field entirely).
 *
 * `0` is NOT treated as absent, unlike sub-type. It means the agent looked and
 * had not found a machine yet, which is worth showing — a device stuck on
 * "Not detected" is a real symptom, and hiding it would make it look like old
 * firmware instead.
 */
export function displayDeviceProtocol(protocol: string | undefined | null): string {
  if (protocol === undefined || protocol === null || protocol === '') return '';
  return DEVICE_PROTOCOL_MAP[protocol] ?? protocol;
}

/**
 * Combined "{sub_type} {type}" label, e.g. "Podium Treadmill", "SSR2 Rower".
 * Falls back to just the type for any machine reporting no submodel.
 */
export function displayDeviceTypeWithSubType(
  type: string | undefined | null,
  subType: string | undefined | null,
): string {
  const typeLabel = displayDeviceType(type);
  const subLabel = displayDeviceSubType(subType);
  return subLabel ? `${subLabel} ${typeLabel}` : typeLabel;
}
